import { FC, useContext, useEffect, useMemo, useRef } from "react";
import { Route, useRouteMatch } from "react-router-dom";
import { ZipProjectContext, ZipProjectContextProvider } from "@app/projects/zip-projects/ZipProjectContext";
import { ZipCodeTable } from "@app/projects/zip-projects/ZipCodeTable";
import { ZipProjectModuleRoutesEnum, ZipProjectRouteComponentProps } from "@app/projects/zip-projects/interfaces";
import { ZipProjectAlertBanners } from "@app/projects/zip-projects/ZipProjectAlertBanners";
import { ZipProjectNotificationsContainer } from "@app/projects/zip-projects/zipProjectNotificationsContainer";
import { ZipProjectModuleNav } from "@app/projects/zip-projects/ZipProjectModuleNav";
import { SummaryRouter } from "@app/projects/network-assessment/summary";
import { NetworkIntegrityPage } from "@app/projects/network-assessment/network-integrity";
import { ZipClassTable } from "@app/projects/zip-projects/ZipClassTable";
import { ZipProjectPerspectiveNav } from "@app/projects/zip-projects/ZipProjectSubModuleNav";
import { ProjectPerspective, ProjectRunConfigurationType } from "../enumerators";
import { ViewableFieldsContainer } from "@app/viewable-fields/ViewableFieldsContainer";
import { ZipProjectProviders } from "@app/projects/zip-projects/ZipProjectProviders";
import { ZipProjectControls } from "@app/projects/zip-projects/ZipProjectControls";
import { ZipProjectTimesliceManagement } from "@app/projects/zip-projects/ZipProjectTimesliceManagement";
import { ZipProjectFilters } from "@app/projects/zip-projects/ZipProjectFilters";
import { ZipChangedGapsFilter } from "@app/projects/zip-projects/ZipChangedGapsFilter";
import useGapFillStatusDescriptions from "@app/projects/network-assessment/adequacy/hooks/useGapFillStatusDescriptions";
import GapFillStatusEditor from "@app/projects/network-assessment/GapFillStatusEditor";
import GapFillStatusRecord from "@app/projects/network-assessment/gapFillStatusRecord";
import { Flip, toast, ToastPosition } from "react-toastify";
import { ExcludeZeroMembership } from "./ExcludeZeroMembership";
import { ZipProjectExceptions } from "./ZipProjectExceptions";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderLeft from "@app/shared/PageHeaderLeft";
import PageInner from "@app/shared/PageInner";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import CheckboxDropdown from "@app/shared/checkbox-dropdown/CheckboxDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { UserContext } from "@app/user/UserContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { ZipCoverageMapRoute } from "@app/projects/zip-projects/maps/ZipCoverageMapRoute";
import { ZipCodeDisplayModel } from "./interfaces/zipCodeDisplayModel";
import ProviderHubLandingPage from "@app/projects/network-assessment/provider-hub/ProviderHubLandingPage";
import { shouldShowZipProviderHub, shouldUseComparedZipDetails } from "@app/projects/zip-projects/utility";

export const WrappedZipProjectOverview: FC = () => {
  const projectContext = useContext(ZipProjectContext);
  const { state } = projectContext;
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const match = useRouteMatch<ZipProjectRouteComponentProps>();
  const isZipGapResolutionStatusEnabled = enabledFeatureFlags.has(Features.ZIP_GAP_RESOLUTION_STATUS);

  ZipProjectNotificationsContainer();

  // The Maps tab/route for Zip projects is feature-flagged while the ZIP map is built out.
  const showMapsTab = enabledFeatureFlags.has(Features.ZIP_CODE_PROJECT_MAPS);

  const showProviderHub = shouldShowZipProviderHub(enabledFeatureFlags, state);

  const statusContainerRef = useRef<HTMLDivElement>(null);
  let statusToastId: string | number = undefined;
  let canRenderStatusToast = true;

  const removeStatusToast = () => {
    if (!!statusToastId) {
      canRenderStatusToast = false;
      toast.dismiss(statusToastId);
      statusToastId = undefined;
    }
  }

  const renderStatusToast = () => {
    if (statusContainerRef.current && statusContainerRef.current.querySelectorAll(".qes-alert").length > 0 && canRenderStatusToast) {
      const statusContainer = statusContainerRef.current;
      const offScreen = statusContainer.getBoundingClientRect().bottom < (16 * 3.5); // 3.5rem, the height of looper's nav bar
      const toastSettings = {
        autoClose: false as const, // TS said it expects "number | false" and a boolean wasn't acceptable, so...
        closeButton: true,
        closeOnClick: true,
        containerId: "z-index-1030",
        hideProgressBar: true,
        // The -110 is somewhat arbitrary, but it should scroll up over the project title
        onClick: () => window.scrollTo({ behavior: "smooth", top: statusContainer.getBoundingClientRect().top + document.documentElement.scrollTop - 110 }),
        position: "top-center" as ToastPosition,
        transition: Flip,
      };
      if (offScreen) {
        if (!statusToastId) {
          const childClasses = Array.from(statusContainer.querySelectorAll(".qes-alert")).reduce((result, child) => result += child.className, "");
          const toastString = "Click here to view project notifications.";
          if (childClasses.includes("danger")) {
            statusToastId = toast.error(toastString, toastSettings);
          } else if (childClasses.includes("warning")) {
            statusToastId = toast.warn(toastString, toastSettings);
          } else {
            statusToastId = toast.info(toastString, toastSettings);
          }
        }
      } else {
        removeStatusToast();
      }
    } else {
      removeStatusToast();
    }
  }

  const gapFillStatusDescriptionMap = useGapFillStatusDescriptions();
  useEffect(() => {
    window.addEventListener("scroll", renderStatusToast);

    return () => {
      window.removeEventListener("scroll", renderStatusToast);
      removeStatusToast();
    };
  }, []);

  useEffect(() => {
    if (!state.project) {
      projectContext.onLoadPage(match.params.projectId);
    }
  }, []);

  const submodule = useMemo(() => {
    switch (state.perspective) {
      case ProjectPerspective.ByZipCode:
        return <ZipCodeTable />;
      case ProjectPerspective.ByZipClass:
        return <ZipClassTable />;
    }
  }, [state.perspective, gapFillStatusDescriptionMap]);

  const dualMembershipOptions: DropdownItem<string>[] = useMemo(() => {
    if (!state.project?.projectRunConfigurations || state.runConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.Standard) {
      return [];
    }

    const companionRunConfiguration = state.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === state.runConfiguration.companionProjectRunConfigurationId);
    const primary = state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary ? state.runConfiguration : companionRunConfiguration;
    const secondary = primary.projectRunConfigurationId === companionRunConfiguration.projectRunConfigurationId ? state.runConfiguration : companionRunConfiguration;
    return [
      {
        key: primary.projectRunConfigurationId.toString(),
        name: primary.memberSetAlias || primary.memberSetName,
      },
      {
        key: secondary.projectRunConfigurationId.toString(),
        name: secondary.memberSetAlias || secondary.memberSetName,
      },
    ];
  }, [state.project, state.runConfiguration]);

  const selectedMemberships = useMemo(() => {
    if (!state.runConfiguration) {
      return [];
    }
    const selection = [{
      key: state.runConfiguration.projectRunConfigurationId.toString(),
      name: state.runConfiguration.memberSetAlias || state.runConfiguration.memberSetName,
    }];

    if (state.isDualMembershipMode && !!state.dualMembershipRunConfiguration) {
      selection.push({
        key: state.dualMembershipRunConfiguration.projectRunConfigurationId.toString(),
        name: state.dualMembershipRunConfiguration.memberSetAlias || state.dualMembershipRunConfiguration.memberSetName,
      })
    }

    return selection;
  }, [state.runConfiguration, state.isDualMembershipMode, state.dualMembershipRunConfiguration]);

  const zipDetailsForMembership = useMemo(() => {
    return shouldUseComparedZipDetails(state.isDualMembershipMode, state.isCompareMode, state.runConfiguration?.projectRunConfigurationType)
      ? state.zipDetailsCompared
      : state.zipDetails;
  }, [state.isDualMembershipMode, state.isCompareMode, state.runConfiguration, state.zipDetails, state.zipDetailsCompared]);

  const gapFillStatusRecord = useMemo((): GapFillStatusRecord | undefined => {
    if (state.perspective !== ProjectPerspective.ByZipCode || !state.isDrilledIn || state.isDualMembershipMode) {
      return undefined;
    }

    const zipDetail = (zipDetailsForMembership as ZipCodeDisplayModel[])?.find((z) => z.specialtyGroupCode === state.filterSelections.specialtyGroupCode?.key);

    if (!zipDetail) {
      return undefined;
    }

    return {
      gapFillStatus: zipDetail.gapFillStatus,
      gapFillStatusComment: zipDetail.gapFillStatusComment,
      gapFillStatusUpdatedDate: zipDetail.gapFillStatusUpdatedDate,
      metCriteria: zipDetail.metPassPercent || zipDetail.membershipCount === 0,
      regionSetId: zipDetail.regionSetId,
      specialtyGroupCode: zipDetail.specialtyGroupCode,
    };
  }, [state.perspective, state.isDrilledIn, zipDetailsForMembership, state.filterSelections, state.isDualMembershipMode]);

  const specialtyGroupName = useMemo((): string | undefined => {
    if (state.perspective !== ProjectPerspective.ByZipCode || !state.isDrilledIn) {
      return undefined;
    }
    return (zipDetailsForMembership as ZipCodeDisplayModel[])?.find((z) => z.specialtyGroupCode === state.filterSelections.specialtyGroupCode?.key)?.specialtyGroupDescription;
  }, [state.perspective, state.isDrilledIn, zipDetailsForMembership, state.filterSelections]);

  if (state.error) {
    if ("message" in state.error) {
      // runtime errors
      throw new Error(state.error.message);
    } else {
      // API errors
      throw { status: state.error.status, statusText: state.error.statusText, traceId: state.error.responseJSON?.traceId };
    }
  }

  const isEditable = (state.runConfiguration && state.isDrilledIn) ?
    state.runConfiguration.isLatestPublished &&
    !state.runConfiguration.isProjected &&
    !state.runConfiguration.isTermed &&
    !state.runConfiguration.isProjectedTermed &&
    !state.project.isEditRestricted
    : false;

  return (
    <Page documentTitle={`${state.project?.resource.name || "Loading"}`}>
      <Breadcrumbs template={BreadcrumbTemplateEnum.NetworkAssessment} items={[state.project?.resource.name || ""]} />
      <PageInner>
        <PageHeader title={state.project?.resource.name || ""} disableHR={true}>
          {!!state.project && (
            <PageHeaderLeft>
              <ZipProjectControls />
            </PageHeaderLeft>
          )}
        </PageHeader>
        <div ref={statusContainerRef}>
          <ZipProjectAlertBanners />
        </div>
        {!!state.project?.projectId && !!state.runConfiguration ? (
          <>
            <ZipProjectModuleNav />
            <Route exact={false} path={`${match.path}/${ZipProjectModuleRoutesEnum.Adequacy}`}>
              <div className="mt-3 zip-project-overview">
                <ZipProjectPerspectiveNav />
                <div className="mt-4">
                  {!!state.runConfiguration.companionProjectRunConfigurationId
                    && (state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary
                      || state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental)
                    && (
                      <TooltipWrapper text={state.isCompareMode ? "Membership selection is disabled when comparing time slices" : ""}>
                        <CheckboxDropdown
                          buttonText={state.isDualMembershipMode ? "Display: Both Memberships" : `Display: ${state.runConfiguration.memberSetAlias}`}
                          cancelText="Cancel"
                          disabled={state.loading || state.isCompareMode}
                          disableEmptySelection={true}
                          loading={state.loading}
                          items={dualMembershipOptions}
                          onSubmit={(selectedItems: Array<DropdownItem<ProjectRunConfigurationType>>) => projectContext.onChangeSelectedMemberships(selectedItems)}
                          selectedItems={selectedMemberships}
                          submitText="Submit"
                        />
                      </TooltipWrapper>
                    )}
                  <ZipProjectTimesliceManagement />
                </div>
                <div className="mt-4">
                  <ZipProjectFilters />
                </div>
                <div className="me-2 d-flex justify-content-end">
                  <ExcludeZeroMembership />
                  <ZipChangedGapsFilter />
                </div>
                {submodule}
                {state.perspective === ProjectPerspective.ByZipCode && state.isDrilledIn && (
                  <>
                    {!state.isCompareMode && !state.isDualMembershipMode && (
                      <ZipProjectExceptions />
                    )}
                    {isZipGapResolutionStatusEnabled && gapFillStatusRecord && !state.isCompareMode && !state.isDualMembershipMode && isEditable && (
                      <GapFillStatusEditor
                        clearCacheImpactAnalysisRunConfigurations={() => projectContext.onChangeTablePage(state.skip)}
                        customProjectId={state.project.projectId}
                        projectRunConfigurationId={state.runConfiguration.projectRunConfigurationId}
                        record={gapFillStatusRecord}
                        gapFillDescriptionMap={gapFillStatusDescriptionMap}
                        specialtyGroupName={specialtyGroupName}
                        specialtyGroupCode={gapFillStatusRecord.specialtyGroupCode} />
                    )}
                    <div className="mt-5">
                      <ViewableFieldsContainer
                        customProjectId={state.project.projectId}
                        projectRunConfigurationId={state.runConfiguration.projectRunConfigurationId}
                        specialtyGroupCode={state.filterSelections.specialtyGroupCode.key}
                      >
                        <ZipProjectProviders />
                      </ViewableFieldsContainer>
                    </div>
                  </>
                )}
              </div>
            </Route>
            <Route exact={false} path={`${match.path}/${ZipProjectModuleRoutesEnum.NetworkIntegrity}`}>
              <div className="tab-content overview-tab-content">
                <NetworkIntegrityPage projectRunConfigurationId={state.runConfiguration.projectRunConfigurationId} isZipProject={true}/>
              </div>
            </Route>
            {showMapsTab && (
              <Route exact={false} path={`${match.path}/${ZipProjectModuleRoutesEnum.Mapping}`}>
                <ZipCoverageMapRoute />
              </Route>
            )}
            {showProviderHub && (
              <Route exact={false} path={`${match.path}/${ZipProjectModuleRoutesEnum.ProviderHub}`}>
                <ProviderHubLandingPage />
              </Route>
            )}
            <Route exact={false} path={`${match.path}/${ZipProjectModuleRoutesEnum.Summary}`} component={SummaryRouter} />
          </>
        ) : state.loading ? <LoadingSpinner /> : null}
      </PageInner>
    </Page >
  );
}

const ZipProjectOverview: FC = () => {
  const user = useContext(UserContext);
  if (!user) {
    throw 403;
  }
  return (
    <ZipProjectContextProvider user={user}>
      <WrappedZipProjectOverview />
    </ZipProjectContextProvider>
  )
}

export default ZipProjectOverview;
